
import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchOrganizationById } from "@/services/organization/organizationService";
import { fetchClientGrowth } from "@/services/landexpand/landExpandService";
import { GrowthOpportunityDetails } from "@/components/landexpand/GrowthOpportunityDetails";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function GrowthOpportunityView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: organization, isLoading: orgLoading } = useQuery({
    queryKey: ['organizations', id],
    queryFn: () => fetchOrganizationById(id || ''),
    enabled: !!id
  });

  const { data: growthData, isLoading: growthLoading } = useQuery({
    queryKey: ['clientGrowth', id],
    queryFn: () => fetchClientGrowth(id || ''),
    enabled: !!id
  });

  const isLoading = orgLoading || growthLoading;

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <Button 
          variant="ghost" 
          size="sm" 
          className="mr-2" 
          onClick={() => navigate('/land-expand')}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">
          {isLoading ? <Skeleton className="h-8 w-48" /> : organization?.name} Growth Opportunity
        </h1>
      </div>
      
      <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
        <div className="md:col-span-1">
          <div className="bg-white border rounded-lg p-4 shadow-sm">
            {isLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ) : (
              <>
                <h2 className="text-lg font-medium mb-2">Organization Details</h2>
                <dl className="space-y-2">
                  <div>
                    <dt className="text-sm text-muted-foreground">Industry</dt>
                    <dd className="text-sm font-medium">{organization?.industry || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Client Tier</dt>
                    <dd className="text-sm font-medium capitalize">{organization?.tier || 'Standard'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Growth Potential</dt>
                    <dd className="text-sm font-medium">{growthData?.growthPotential || 0}%</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Expansion Readiness</dt>
                    <dd className="text-sm font-medium">{growthData?.expansionReadiness || 'Low'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Last Review Date</dt>
                    <dd className="text-sm font-medium">{growthData?.lastReviewDate || 'Never'}</dd>
                  </div>
                </dl>
                {growthData?.strategyNotes && (
                  <div className="mt-4">
                    <h3 className="text-sm font-medium">Strategy Notes</h3>
                    <p className="text-sm mt-1 p-2 bg-muted rounded-md">{growthData.strategyNotes}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        
        <div className="md:col-span-2">
          {id ? <GrowthOpportunityDetails organizationId={id} /> : <Skeleton className="h-[400px]" />}
        </div>
      </div>
    </div>
  );
}
